import { Grid, List, ListItem, ListItemText, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import React, { useContext, useEffect, useState } from "react";
import DialogConfirm from "../../../components/common/DialogConfirm";
import UserContext from "../../../context/user-context";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  userResetSelectVehicle,
  userSelectVehicle,
  userShowCompanionSection,
  userShowDialogCompanion,
} from "../../../store/actions/userAction";
import { EntrantTabIndex } from "../../../utils/tabIndex";
import NewVehicle from "./NewVehicle/NewVehicle";
import { useStyles } from "./VehicleStyles";
import {setOpenedVehicleSection} from "../../../redux/actions/registerExitActions";
import {useDispatch} from "react-redux";
import useVehicleSearch, { MIN_PLATE_SEARCH_LENGTH } from "../../../hooks/useVehicleSearch";
import { FindVehicleResult } from "../../../passapp-sdk/vehicle";
import TextFieldOutlined from "../../../components/common/TextFieldOutlined";
import VehicleSummaryCard from "../../../views/LogisticPark/RegistroIngreso/components/AccessVehicleForm/VehicleContentForm/VehicleSummaryCard";

export type VehicleData = {
  id: string;
  insuranceExpirationDate: string;
  plate: string;
  model: string;
  color: string;
  brand: string;
  insurancePhoto: string;
  insurancePhotoNew: any;
};

const Vehicle = () => {
  const classes = useStyles();
  const { state, dispatch } = useContext(UserContext);
  const [vehicleSelected, setVehicleSelected] = useState<any>(null);
  const [openVehicleSection, setOpenVehicleSection] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [searchSelectionError, setSearchSelectionError] = useState<string | null>(null);
  const { t, BundleKey } = useTranslation();
  const isEntry = state.config?.eventType === "in";
  const {
    results: vehicleResults,
    loading: isSearchingVehicles,
    searchError,
    selecting: isSelectingVehicle,
    selectVehicle: selectVehicleFromApi,
  } = useVehicleSearch(state.id, searchTerm, searchRefreshKey);

  const normalizePlate = (plate: string) => plate.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const normalizedSearch = normalizePlate(searchTerm);
  const hasMinimumSearchLength = normalizedSearch.length >= MIN_PLATE_SEARCH_LENGTH;
  const hasExactMatch = vehicleResults.some(
    (vehicle) => normalizePlate(vehicle.plate) === normalizedSearch
  );

  const showNewVehicle = (plate = "") => {
    setOpenVehicleSection(true);
    setVehicleSelected({
      id: "",
      insuranceExpirationDate: "",
      plate: normalizePlate(plate),
      model: "",
      color: "",
      brand: "",
      insurancePhoto: "",
      insurancePhotoNew: "",
    });
  };

  const selectVehicle = (vehicle: any) => {
    if (state.vehicleSelected && state.vehicleSelected.id === vehicle.id) {
      if (vehicle.insuranceExpirationDate !== state.vehicleSelected.insuranceExpirationDate) {
        setVehicleSelected({
          ...state.vehicleSelected,
          insuranceExpirationDate: vehicle.insuranceExpirationDate
        });
      } else {
        setVehicleSelected(state.vehicleSelected);
      }
    }
    else setVehicleSelected(vehicle);
    setOpenVehicleSection(true);
  };

  /** La fila de findVehicles no tiene la ficha suficiente para editar ni para el evento. La
   * selección siempre se confirma contra selectVehicle, que además sincroniza los cambios que la
   * persona haya hecho desde su aplicación. */
  const handleSelectSearchResult = async (candidate: FindVehicleResult) => {
    if (isSelectingVehicle) return;
    setSearchSelectionError(null);
    const result = await selectVehicleFromApi(candidate.id);
    if (!result.success) {
      setSearchSelectionError(result.error);
      return;
    }
    dispatch(userResetSelectVehicle());
    dispatch(userSelectVehicle(result.vehicle));
    setVehicleSelected(result.vehicle);
    setOpenVehicleSection(true);
  };

  const onCompanions = () => {
    dispatch(userShowCompanionSection(true));
    dispatch(userShowDialogCompanion(false));
  };

  const handleClose = () => {
    dispatch(userShowDialogCompanion(false));
  };

  const goBack = () => {
    setOpenVehicleSection(false);
  };

  const refreshSearchResults = () => {
    setSearchRefreshKey((current) => current + 1);
  };

  // brand/model/color pueden venir undefined en vehículos viejos; un template literal los
  // imprimiría como el string literal "undefined".
  const vehicleLabel = (vehicle: any) => {
    const brandModel = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
    const withColor = [brandModel, vehicle.color].filter(Boolean).join(" - ");
    return `${withColor} (${vehicle.plate})`.trim();
  };

  // Con un vehículo ya elegido el buscador se reemplaza por su resumen (como en el registro de
  // ingreso del parque logístico): editar reabre la ficha y quitar vuelve a buscar. Vale también
  // para egreso: un vehículo recién dado de alta no está en `state.vehicles`, así que sin la
  // tarjeta quedaba seleccionado pero invisible.
  const selectedEntryVehicle = state.vehicleSelected?.id ? state.vehicleSelected : null;

  const handleEditSelectedVehicle = () => {
    setVehicleSelected(state.vehicleSelected);
    setOpenVehicleSection(true);
  };

  const handleRemoveSelectedVehicle = () => {
    dispatch(userResetSelectVehicle());
    setSearchTerm("");
    setSearchSelectionError(null);
  };

  useEffect(() => {
    return () => {
      dispatch(userResetSelectVehicle());
    };
  }, []);

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        {openVehicleSection ? (
          <Grid item>
            <NewVehicle
              goBack={goBack}
              vehicle={vehicleSelected}
              onSaved={refreshSearchResults}
            />
          </Grid>
        ) : (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.entryVehicleContent}
          >
            <Grid
              item
              className={
                selectedEntryVehicle ? classes.entryVehicleSelected : classes.entryVehicleSearch
              }
            >
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
              >
                <Grid item className={selectedEntryVehicle ? classes.entryVehicleSelected : undefined}>
                  {selectedEntryVehicle ? (
                    <VehicleSummaryCard
                      plate={selectedEntryVehicle.plate}
                      vehicleInfo={[selectedEntryVehicle.brand, selectedEntryVehicle.model]
                        .filter(Boolean)
                        .join(" ")}
                      isActionsDisabled={false}
                      onEdit={handleEditSelectedVehicle}
                      onDelete={handleRemoveSelectedVehicle}
                    />
                  ) : (
                    <>
                      <TextFieldOutlined
                        value={searchTerm}
                        name="vehicleSearch"
                        type="text"
                        label="Buscar vehículo por patente"
                        onChange={(value) => {
                          setSearchTerm(value);
                          setSearchSelectionError(null);
                        }}
                        InputProps={{ tabIndex: EntrantTabIndex.vehicleFromTheList }}
                      />
                      {!hasMinimumSearchLength ? (
                        <Typography variant="caption">
                          Ingresá al menos {MIN_PLATE_SEARCH_LENGTH} caracteres de la patente
                        </Typography>
                      ) : isSearchingVehicles ? (
                        <Typography variant="caption">Buscando vehículos...</Typography>
                      ) : (
                        <List
                          component="nav"
                          aria-label="Resultados de vehículos"
                          className={classes.entryVehicleResults}
                        >
                          {vehicleResults.map((vehicle) => (
                            <ListItem
                              key={vehicle.id}
                              button
                              selected={state.vehicleSelected?.id === vehicle.id}
                              onClick={() => handleSelectSearchResult(vehicle)}
                              disabled={isSelectingVehicle}
                            >
                              <ListItemText primary={vehicleLabel(vehicle)} />
                            </ListItem>
                          ))}
                          {!vehicleResults.length && (
                            <ListItem>
                              <ListItemText primary="No se encontraron vehículos con esa patente" />
                            </ListItem>
                          )}
                          {!hasExactMatch && (
                            <ListItem
                              button
                              className={classes.entryVehicleAddOption}
                              tabIndex={EntrantTabIndex.addVehicle}
                              onClick={() => showNewVehicle(searchTerm)}
                            >
                              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                              <ListItemText
                                primary={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_ADD)} "${normalizedSearch}"`}
                              />
                            </ListItem>
                          )}
                        </List>
                      )}
                      {(searchError || searchSelectionError) && (
                        <Typography color="error" variant="caption">
                          {searchError || searchSelectionError}
                        </Typography>
                      )}
                      {/* Egreso conserva la lista heredada, pero el buscador va siempre: un usuario
                          sin patentes vinculadas tiene que poder buscar y, si no hay match, dar de
                          alta desde el propio buscador. */}
                      {!isEntry && !!state.vehicles?.length && (
                        <List component="nav" aria-label="secondary mailbox folder">
                          {state.vehicles.map((vehicle: any, index: any) => (
                            <ListItem
                              key={index}
                              tabIndex={EntrantTabIndex.vehicleFromTheList}
                              button
                              onClick={() => selectVehicle(vehicle)}
                            >
                              <ListItemText primary={vehicleLabel(vehicle)} />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <DialogConfirm
        openDialog={state.showDialogCompanion || false}
        handleConfirm={onCompanions}
        handleClose={handleClose}
        title={t(BundleKey.DIALOG_COMPANIONENTRY_TITLE)}
        message={t(BundleKey.DIALOG_COMPANIONENTRY_MESSAGE)}
        titleButtonOk={t(BundleKey.ACTION_YES)}
        titleButtonNot={t(BundleKey.ACTION_NO)}
      />
    </>
  );
};

export default Vehicle;
